import type BaseModel from "../../../src/models/BaseModel";
import type { C1_EloItemInstanceData } from "../types/C1_EloTypes";
import { C1_BaseObject } from "./C1_BaseObject";
import type { C1_DefinitionItem } from "./C1_DefinitionItem";
import { resolveEloDefinitionPath } from "./C1_eloDefinitionPath";

/** Common base for definition-backed C1 facets on ESTADA groups, consumers and buttons. */
export abstract class C1_BaseObject2 extends C1_BaseObject<C1_EloItemInstanceData> {
  /** Unique runtime path of the referenced expanded definition. */
  _defId = "";

  constructor(data: C1_EloItemInstanceData, owner: BaseModel) {
    super(data, owner);
    this._data = data;
    this._defId = data._defId || "";
  }

  /** Resolves the referenced definition through ESTADA's central object registry. */
  get definition(): C1_DefinitionItem | undefined {
    return state.objectRegistry.getObject<C1_DefinitionItem>(resolveEloDefinitionPath(this._defId));
  }

  set definition(value: C1_DefinitionItem | undefined) {
    const newDefinitionId = value?.id || "";
    if (this._defId === newDefinitionId) return;
    this.beforeChange("definition");
    this._defId = newDefinitionId;
    this._data._defId = newDefinitionId;
    this.changed("definition");
  }

  /** Original catalog ID of the resolved definition. */
  get definitionId(): string {
    return this.definition?.definitionId ?? "";
  }

  /** Declares the facet payload and definition reference as persistent owner properties. */
  getPersistentProperties(): string[] {
    return ["_data", "_defId"];
  }

  /** Serializes all editable facet values together with their definition reference. */
  override toJSON(): C1_EloItemInstanceData {
    return { ...this._data, _defId: this._defId };
  }

  /** Restores facet values and synchronizes the runtime definition reference. */
  override fromJSON(input: unknown): void {
    const data = input as C1_EloItemInstanceData;
    this._data = { ...data };
    this._defId = data._defId || "";
  }
}
